import { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { useCreatePersonEvent } from '../hooks/useChurchAdmin'
import { geocodeAddress } from '../lib/geocode'
import { EventTimePicker } from '../components/ui/DateTimePicker'
import { ApiError } from '../lib/api'
import { EVENT_TYPES, EventTypeKey } from '../components/ui/eventTypes'
import { PublicLayout } from '../components/layout/PublicLayout'
import { Icons } from '../components/ui/Icons'
import LocationPicker from '../components/LocationPicker'
import { useCurrentPosition } from '../hooks/useCurrentPosition'
import { ImageUpload } from '../components/admin/ImageUpload'
import { CapabilityToggleRow } from '../components/event/CapabilityToggleRow'
import { DishGoalsFields } from '../components/event/DishGoalsFields'
import { OutdoorDetailsFields } from '../components/event/OutdoorDetailsFields'
import type { EventType, EventAttributes } from '@fellowship/shared'

const EVENT_TYPE_KEYS = Object.keys(EVENT_TYPES) as EventTypeKey[]

export const ATTRIBUTE_OPTIONS: { key: keyof EventAttributes; label: string }[] = [
  { key: 'kidFriendly', label: 'Kid friendly' },
  { key: 'petFriendly', label: 'Pet friendly' },
  { key: 'vegetarianFriendly', label: 'Vegetarian' },
  { key: 'veganFriendly', label: 'Vegan' },
  { key: 'glutenFreeOptions', label: 'Gluten-free options' },
  { key: 'allWelcome', label: 'All welcome' },
  { key: 'noMeat', label: 'No meat' },
  { key: 'noAlcohol', label: 'No alcohol' },
  { key: 'noTobacco', label: 'No tobacco' },
  { key: 'accessibilityFriendly', label: 'Accessible' },
]


function Field({
  label, htmlFor, hint, error, required, children,
}: {
  label: string
  htmlFor?: string
  hint?: string
  error?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-[13px] font-semibold text-ink-2 flex gap-1">
        {label}
        {required && <span className="text-warn">*</span>}
      </label>
      {hint && <div className="text-[11.5px] text-muted -mt-1">{hint}</div>}
      {children}
      {error && <div className="text-[12px] text-[#A33A2A]">{error}</div>}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-line-soft rounded-2xl px-4 sm:px-6 py-[22px]">
      <div className="text-[13px] font-bold text-ink-2 tracking-[0.04em] uppercase mb-[18px]">
        {title}
      </div>
      {children}
    </div>
  )
}

export default function CreatePersonEventPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const currentPosition = useCurrentPosition()
  const createEvent = useCreatePersonEvent()

  const [eventType, setEventType] = useState<EventType>('potluck')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [locationName, setLocationName] = useState('')
  const [addressText, setAddressText] = useState('')
  const [locationLat, setLocationLat] = useState<number | null>(null)
  const [locationLng, setLocationLng] = useState<number | null>(null)
  const [locationMode, setLocationMode] = useState<'private' | 'venue' | 'pin'>('private')
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'invite_only'>('public')
  const [attributes, setAttributes] = useState<EventAttributes>({ bringADish: true })
  const [maxAttendees, setMaxAttendees] = useState('')
  const [rsvpRequiresApproval, setRsvpRequiresApproval] = useState(true)
  const [hideAddressUntilApproved, setHideAddressUntilApproved] = useState(true)
  const [mainGoal, setMainGoal] = useState(4)
  const [saladGoal, setSaladGoal] = useState(3)
  const [sideGoal, setSideGoal] = useState(2)
  const [dessertGoal, setDessertGoal] = useState(2)
  const [drinkGoal, setDrinkGoal] = useState(2)
  const [outdoorDifficulty, setOutdoorDifficulty] = useState<'easy' | 'moderate' | 'strenuous'>('easy')
  const [outdoorDistance, setOutdoorDistance] = useState('')
  const [outdoorElevation, setOutdoorElevation] = useState('')
  const [outdoorMeetupNote, setOutdoorMeetupNote] = useState('')
  const [studyTopic, setStudyTopic] = useState('')
  const [studyScripture, setStudyScripture] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [geocodeError, setGeocodeError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  function handleTypeChange(key: EventType) {
    setEventType(key)
    setAttributes((prev) => ({
      ...prev,
      bringADish: key === 'potluck' ? true : undefined,
      outdoorActivity: key === 'outdoors' ? true : undefined,
    }))
  }

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Title is required'
    if (!startsAt) errs.startsAt = 'Start date and time is required'
    const hasPinCoords = locationMode === 'pin' && locationLat !== null && locationLng !== null
    if (!addressText.trim() && !hasPinCoords) errs.addressText = 'Address is required'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  function buildMetadata() {
    const meta: Record<string, unknown> = {}
    if (attributes.bringADish) {
      const dishGoals = {
        ...(mainGoal > 0 ? { main: mainGoal } : {}),
        ...(saladGoal > 0 ? { salad: saladGoal } : {}),
        ...(sideGoal > 0 ? { side: sideGoal } : {}),
        ...(dessertGoal > 0 ? { dessert: dessertGoal } : {}),
        ...(drinkGoal > 0 ? { drink: drinkGoal } : {}),
      }
      if (Object.keys(dishGoals).length > 0) meta.dishGoals = dishGoals
    }
    if (attributes.outdoorActivity) {
      meta.difficulty = outdoorDifficulty
      if (outdoorDistance) meta.distanceMiles = parseFloat(outdoorDistance)
      if (outdoorElevation) meta.elevationFt = parseInt(outdoorElevation)
      if (outdoorMeetupNote.trim()) meta.meetupNote = outdoorMeetupNote.trim()
    }
    if (eventType === 'study') {
      if (studyTopic.trim()) meta.topic = studyTopic.trim()
      if (studyScripture.trim()) meta.scriptureRef = studyScripture.trim()
    }
    return Object.keys(meta).length > 0 ? meta : undefined
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!validate()) return
    setGeocodeError('')
    setIsSubmitting(true)
    const coords = locationLat !== null && locationLng !== null
      ? { lat: locationLat, lng: locationLng }
      : await geocodeAddress(addressText.trim())
    if (!coords) {
      setGeocodeError('Could not locate that address. Use the "Locate →" button or drag the map pin.')
      setIsSubmitting(false)
      return
    }
    createEvent.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        eventType,
        locationName: locationName.trim() || undefined,
        addressText: addressText.trim(),
        locationLat: coords.lat,
        locationLng: coords.lng,
        visibility,
        rsvpRequiresApproval,
        hideAddressUntilApproved,
        maxAttendees: maxAttendees ? parseInt(maxAttendees) : undefined,
        isRecurring: false,
        tags: [],
        imageUrl: imageUrl ?? undefined,
        attributes,
        metadata: buildMetadata(),
        startsAt: new Date(startsAt).toISOString(),
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
      },
      {
        onSuccess: ({ data }) => {
          const occurrenceId = data.occurrences[0]?.id
          if (occurrenceId) navigate(`/events/${data.event.id}`)
          else navigate('/app')
        },
        onSettled: () => setIsSubmitting(false),
      },
    )
  }

  const serverError = createEvent.error instanceof ApiError ? createEvent.error.message : null

  const VISIBILITY_OPTIONS = [
    { value: 'public' as const, label: 'Public', description: 'Appears in search and map for everyone', Icon: Icons.globe },
    { value: 'unlisted' as const, label: 'Unlisted', description: 'Share a link — anyone with it can RSVP, no account needed', Icon: Icons.link },
    { value: 'invite_only' as const, label: 'Invite only', description: 'Invited members only — guests must have an account', Icon: Icons.lock },
  ]

  return (
    <PublicLayout>
      <div className="max-w-[640px] mx-auto px-4 sm:px-6 pt-10 pb-20 overflow-x-hidden">
        <div className="eyebrow mb-2">Create</div>
        <h1 className="serif m-0 mb-8 text-[38px] tracking-[-0.02em]">
          Host an{' '}
          <span className="serif-i text-primary">event</span>
        </h1>

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">

          {(serverError || geocodeError) && (
            <div role="alert" className="px-4 py-3 rounded-xl bg-danger-soft border border-danger-line text-danger-deep text-[13px]">
              {serverError ?? geocodeError}
            </div>
          )}

          {/* ── Type picker ───────────────────────────────────────────────── */}
          <Section title="Event type">
            <div className="grid grid-cols-3 gap-2.5">
              {EVENT_TYPE_KEYS.map((key) => {
                const t = EVENT_TYPES[key]
                const selected = eventType === key
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleTypeChange(key as EventType)}
                    aria-pressed={selected}
                    aria-label={t.label}
                    className="p-[14px_12px] rounded-[14px] cursor-pointer text-left flex flex-col gap-1.5 font-sans border-[1.5px]"
                    style={{
                      background: selected ? t.soft : 'var(--surface-2)',
                      borderColor: selected ? t.color : 'var(--line-soft)',
                    }}
                  >
                    <div
                      className="w-8 h-8 rounded-[9px] inline-flex items-center justify-center text-[16px]"
                      style={{ background: selected ? t.color : t.soft, color: selected ? '#fff' : t.color }}
                    >{t.glyph}</div>
                    <div className="text-[13px] font-semibold">{t.label}</div>
                  </button>
                )
              })}
            </div>
          </Section>

          {/* ── Details ──────────────────────────────────────────────────── */}
          <Section title="Details">
            <div className="flex flex-col gap-4">
              <Field label="Title" htmlFor="title" required error={errors.title}>
                <input
                  id="title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Saturday morning hike"
                  aria-invalid={Boolean(errors.title)}
                  className="input"
                />
              </Field>
              <Field label="Description" htmlFor="description">
                <textarea
                  id="description"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What guests can expect, what to bring…"
                  className="input resize-y h-auto py-3 px-3.5 leading-[1.5]"
                />
              </Field>
            </div>
          </Section>

          {/* ── When ─────────────────────────────────────────────────────── */}
          <Section title="When">
            <EventTimePicker
              startsAt={startsAt}
              endsAt={endsAt}
              onStartsAtChange={setStartsAt}
              onEndsAtChange={setEndsAt}
              startsAtError={errors.startsAt}
            />
          </Section>

          {/* ── Where ────────────────────────────────────────────────────── */}
          <Section title="Where">
            <LocationPicker
              addressText={addressText}
              onAddressChange={setAddressText}
              lat={locationLat}
              lng={locationLng}
              onCoordinatesChange={(lat, lng) => { setLocationLat(lat); setLocationLng(lng) }}
              addressError={errors.addressText}
              showModeToggle
              mode={locationMode}
              onModeChange={setLocationMode}
              onHideAddressChange={setHideAddressUntilApproved}
              onLocationNameChange={setLocationName}
              defaultLat={currentPosition?.lat ?? user?.locationLat}
              defaultLng={currentPosition?.lng ?? user?.locationLng}
            />
          </Section>

          {/* ── Capability toggles + gated sections ─────────────────────── */}
          <CapabilityToggleRow
            bringADish={Boolean(attributes.bringADish)}
            outdoorActivity={Boolean(attributes.outdoorActivity)}
            onToggleDish={() => setAttributes((p) => ({ ...p, bringADish: p.bringADish ? undefined : true }))}
            onToggleOutdoor={() => setAttributes((p) => ({ ...p, outdoorActivity: p.outdoorActivity ? undefined : true }))}
          />

          {attributes.bringADish && (
            <Section title="Dish goals">
              <DishGoalsFields
                mainGoal={mainGoal} setMainGoal={setMainGoal}
                saladGoal={saladGoal} setSaladGoal={setSaladGoal}
                sideGoal={sideGoal} setSideGoal={setSideGoal}
                dessertGoal={dessertGoal} setDessertGoal={setDessertGoal}
                drinkGoal={drinkGoal} setDrinkGoal={setDrinkGoal}
              />
            </Section>
          )}

          {/* ── Outdoor details ───────────────────────────────────────────── */}
          {attributes.outdoorActivity && (
            <Section title="Outdoor details">
              <OutdoorDetailsFields
                difficulty={outdoorDifficulty} setDifficulty={setOutdoorDifficulty}
                distance={outdoorDistance} setDistance={setOutdoorDistance}
                elevation={outdoorElevation} setElevation={setOutdoorElevation}
                meetupNote={outdoorMeetupNote} setMeetupNote={setOutdoorMeetupNote}
              />
            </Section>
          )}

          {/* ── Study details ─────────────────────────────────────────────── */}
          {eventType === 'study' && (
            <Section title="Study details">
              <div className="flex flex-col gap-3.5">
                <Field label="Topic" htmlFor="studyTopic">
                  <input
                    id="studyTopic"
                    type="text"
                    value={studyTopic}
                    onChange={(e) => setStudyTopic(e.target.value)}
                    placeholder="The Sermon on the Mount"
                    className="input"
                  />
                </Field>
                <Field label="Scripture reference" htmlFor="studyScripture">
                  <input
                    id="studyScripture"
                    type="text"
                    value={studyScripture}
                    onChange={(e) => setStudyScripture(e.target.value)}
                    placeholder="Matthew 5–7"
                    className="input"
                  />
                </Field>
              </div>
            </Section>
          )}

          {/* ── Photo ────────────────────────────────────────────────────── */}
          <Section title="Photo">
            {imageUrl ? (
              <div className="flex flex-col gap-2.5">
                <div className="rounded-xl overflow-hidden" style={{ height: 160 }}>
                  <img src={imageUrl} alt="" className="w-full h-full object-cover block" />
                </div>
                <div className="flex gap-4 items-center">
                  <ImageUpload context="event-image" onUpload={setImageUrl}>
                    <span className="text-[13px] text-primary font-medium cursor-pointer font-sans">Change photo</span>
                  </ImageUpload>
                  <button type="button" onClick={() => setImageUrl(null)} className="text-[12px] text-muted bg-transparent border-0 cursor-pointer p-0 font-sans">
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <ImageUpload context="event-image" onUpload={setImageUrl}>
                <div className="h-[108px] border-[1.5px] border-dashed border-line-soft rounded-xl flex flex-col items-center justify-center gap-1 cursor-pointer">
                  <div className="text-[22px] mb-0.5">📷</div>
                  <div className="text-[13px] font-semibold text-ink-2">Add a photo</div>
                  <div className="text-[11.5px] text-muted">JPEG, PNG, or WebP · up to 5 MB</div>
                </div>
              </ImageUpload>
            )}
          </Section>

          {/* ── Attributes ───────────────────────────────────────────────── */}
          <Section title="Attributes">
            <div className="grid grid-cols-2 gap-2">
              {ATTRIBUTE_OPTIONS.map(({ key, label }) => {
                const checked = Boolean(attributes[key])
                return (
                  <label
                    key={key}
                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] cursor-pointer border-[1.5px] ${checked ? 'bg-primary-soft border-primary' : 'bg-s2 border-line-soft'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setAttributes((prev) => ({ ...prev, [key]: e.target.checked || undefined }))}
                      aria-label={label}
                      className="w-[15px] h-[15px] accent-primary cursor-pointer shrink-0"
                    />
                    <span className="text-[13px] font-medium">{label}</span>
                  </label>
                )
              })}
            </div>
          </Section>

          {/* ── Visibility ───────────────────────────────────────────────── */}
          <Section title="Visibility">
            <div className="flex flex-col gap-2">
              {VISIBILITY_OPTIONS.map(({ value: v, label, description, Icon }) => {
                const selected = visibility === v
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setVisibility(v)}
                    aria-pressed={selected}
                    aria-label={label}
                    className={`flex items-center gap-3 px-3.5 py-3 rounded-xl cursor-pointer text-left font-sans border-[1.5px] ${selected ? 'bg-primary-soft border-primary' : 'bg-s2 border-line-soft'}`}
                  >
                    <div
                      className={`w-[34px] h-[34px] rounded-[9px] shrink-0 flex items-center justify-center ${selected ? 'bg-primary text-white' : 'bg-line-soft text-muted'}`}
                    >
                      <Icon size={15} />
                    </div>
                    <div className="flex-1">
                      <div className="text-[13.5px] font-semibold text-ink mb-[1px]">{label}</div>
                      <div className="text-[12px] text-muted">{description}</div>
                    </div>
                    {selected && <Icons.check size={14} className="text-primary shrink-0" />}
                  </button>
                )
              })}
              {visibility === 'invite_only' && (
                <div className="text-[12px] text-muted pl-0.5 mt-0.5">
                  After publishing, you can invite people from the event's manage page.
                </div>
              )}
            </div>
          </Section>

          {/* ── RSVPs ─────────────────────────────────────────────────────── */}
          <Section title="RSVPs">
            <div className="flex flex-col gap-3.5">
              <Field label="Max guests" htmlFor="maxAttendees" hint="Leave blank for no limit.">
                <input
                  id="maxAttendees"
                  type="number"
                  min={1}
                  value={maxAttendees}
                  onChange={(e) => setMaxAttendees(e.target.value)}
                  placeholder="No limit"
                  className="input"
                />
              </Field>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[13.5px] font-medium">Require RSVP approval</div>
                  <div className="text-[12px] text-muted mt-0.5">
                    Approve each request before the address is revealed.
                  </div>
                </div>
                <input
                  type="checkbox"
                  checked={rsvpRequiresApproval}
                  onChange={(e) => {
                    setRsvpRequiresApproval(e.target.checked)
                    if (!e.target.checked) setHideAddressUntilApproved(false)
                  }}
                  aria-label="Require RSVP approval"
                  className="w-[18px] h-[18px] cursor-pointer accent-primary"
                />
              </div>
            </div>
          </Section>


          {/* ── Submit ────────────────────────────────────────────────────── */}
          <button
            type="submit"
            disabled={isSubmitting || createEvent.isPending}
            className={`h-[52px] rounded-[14px] border-0 text-on-primary text-[15px] font-bold cursor-pointer font-sans disabled:cursor-not-allowed ${isSubmitting || createEvent.isPending ? 'bg-muted' : 'bg-primary'}`}
          >
            {isSubmitting || createEvent.isPending ? 'Publishing…' : 'Publish event'}
          </button>
        </form>
      </div>
    </PublicLayout>
  )
}
